import {jsx} from 'panel';

import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {SelectEl} from 'common/components/select';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SectionItem} from 'common/components/items-menu/types';
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {ButtonTheme} from 'common/components/button/constants';
import {hasNewQBUI} from 'common/widgets/query-builder/util';
import {ButtonEl} from 'common/components/button';

import type {QueryEntrySection} from '../index';
import {QueryEntrySectionEl} from '../row-types/base/query-entry-section-el';
import {
  Action,
  EntryPart,
  EntrySectionUpdateEvent,
  FiltersOperator,
  FiltersOperatorDisplayLabels,
  QueryEntry,
  QueryFilterEntry,
  RowLabelType,
  SelectorTypeDefinitions,
} from '../types';
import {getFilterEntriesFromFilters} from '../row-types/filter/utils';

export function RowFilters({
  el,
  entry,
  index,
  source,
  handleUpdate,
  filtersOperators,
}: {
  el: QueryEntrySection;
  entry: QueryEntry;
  index: number;
  source: EntryPart;
  handleUpdate: (ev: EntrySectionUpdateEvent) => void;
  filtersOperators?: FiltersOperator[];
}) {
  const {placeholders} = el.state;
  const event = entry.event as EventItem;
  const showPlaceholder = placeholders.get(index)?.includes(source);
  const filters: PropertyFilter[] = el.helpers.getEntryFilters(index)[source] || [];

  const filterEntries = getFilterEntriesFromFilters(filters, entry.filtersOperator);

  if (filters.length === 0 && !showPlaceholder) {
    return null;
  }

  return (
    <QueryEntrySectionEl
      key={`entry-${source}-section`}
      class={{'indent-filters': true, 'contained-section': true}}
      attrs={{
        ...el.helpers.getNestedSectionAttrs(),
        elref: el.helpers.camelCase(
          `entry-${source}`,
        ) /* entryFilters or entryBehavioralFilters, depending on source */,
        'event-names': event && event.value ? [event.value] : [],
        'disable-add-entry': true,
        'can-rename-step': false,
        'behavior-mode': el.attr(`behavior-mode`),
        'row-types': [SelectorTypeDefinitions.Filter],
        'disable-clone-entry': filters.some((filter) => isFirstTimeEventFilter(filter)),
        'row-label-type': RowLabelType.None,
        'hide-events': !el.helpers.isEventBasedType(entry.type),
        'project-has-cohorts': el.attr(`project-has-cohorts`) && el.attr(`allow-cohort-filter`),
        'inline-cohorts-override': el.attr(`inline-cohorts-override`),
        entries: filterEntries,
      }}
      props={{
        isStagingRow: showPlaceholder && {
          ...placeholders.get(index)[1],
          filtersOperator: filterEntries[0]?.filtersOperator || FiltersOperator.And,
        },
        /** renders the AND/OR switch for inline filters */
        prefixRenderer: ({index: filterIndex, entry: filterEntry}) =>
          RowFiltersPrefix({
            filterIndex,
            filtersOperators,
            filter: new PropertyFilter((filterEntry as QueryFilterEntry).event),
            filtersOperator: entry.filtersOperator || FiltersOperator.And,
            handleChange: (item) =>
              el.updateEntries(Action.Edit, {entry: {...entry, filtersOperator: item.value}, index}),
            isReadOnlyMode: el.attr(`readonly`),
          }),
      }}
      on={{
        update: handleUpdate,
        abortStage: () => el.helpers.setActiveEntry(`close`, index, source),
      }}
    />
  );
}

export function RowFiltersPrefix({
  filtersOperator,
  filter,
  filterIndex,
  handleChange,
  forceDisabled,
  isReadOnlyMode,
  forBehaviors,
  filtersOperators,
}: {
  filtersOperator: FiltersOperator;
  filter: PropertyFilter;
  filterIndex: number;
  handleChange: (item: SectionItem) => void;
  forceDisabled?: boolean;
  isReadOnlyMode: boolean;
  forBehaviors?: boolean;
  filtersOperators?: FiltersOperator[];
}) {
  const hasNewUI = hasNewQBUI();
  let canSwitch = false;
  let label = ``;
  let operators: FiltersOperator[] = [];

  const forExtendedLogicalOperators =
    filtersOperators &&
    (filtersOperators.includes(FiltersOperator.AndAny) || filtersOperators.includes(FiltersOperator.OrAll));
  const isExtendedLogicalOperator =
    filtersOperator === FiltersOperator.AndAny || filtersOperator === FiltersOperator.OrAll;

  // behavior filter as first filter
  if (filter && isFirstTimeEventFilter(filter)) {
    label = `for the`;
    if (hasNewQBUI()) {
      return null;
    }
    // first non-behavior filter
  } else if (filterIndex === 0) {
    label = forBehaviors ? `who` : `where`;
    if (hasNewQBUI()) {
      return null;
    }
  } else {
    label = FiltersOperatorDisplayLabels[filtersOperator];
    canSwitch = true;
    operators = filtersOperators || [FiltersOperator.And, FiltersOperator.Or];
  }

  // and/or switch
  return (
    <div
      sel='.entry-selector-prefix'
      class={{'behavior-mode': forBehaviors, 'for-extended-operators': forExtendedLogicalOperators}}
    >
      {!(isExtendedLogicalOperator && filterIndex > 1) && (
        <SelectEl
          class={{'is-disabled': forceDisabled || !canSwitch}}
          attrs={{
            readonly: isReadOnlyMode,
            elref: `filtersOperatorSelect`,
            theme: ButtonTheme.Tertiary,
            'menu-width': `90`,
            'never-show-caret': true,
            sections: [
              {
                items: operators.map(
                  (operator) =>
                    ({
                      isSelected: filtersOperator && operator === filtersOperator.toString(),
                      value: operator,
                      label: FiltersOperatorDisplayLabels[operator],
                    } as SectionItem),
                ),
              },
            ],
            ...(label
              ? {
                  'selected-option': label,
                }
              : {}),
          }}
          on={{change: (ev: CustomEvent) => handleChange(ev.detail.item as SectionItem)}}
        >
          {hasNewUI && <ButtonEl attrs={{slot: `trigger`, theme: ButtonTheme.Tertiary}}>{label}</ButtonEl>}
        </SelectEl>
      )}
    </div>
  );
}
